import styled from 'styled-components';
import React, { useEffect, useRef, useState } from 'react';
import CryptoJS from "crypto-js";
import { app } from "../config/firebaseConfig";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getFirestore, doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { normalizeInterviewTypeKey } from "../utils/interviewType";
import { fetchInterviewByAccessToken } from "../services/interviewService";
import { caminhoDoLogo, caminhoDoMeta } from '../utils/caminhosConta';

export default function FinishContainer({ company }) {
  const [logoCompanyUrl, setLogoCompanyUrl] = useState('');
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackNotes, setFeedbackNotes] = useState(new Set());
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackSaving, setFeedbackSaving] = useState(false);
  const [feedbackSaved, setFeedbackSaved] = useState(false);
  const [feedbackLoaded, setFeedbackLoaded] = useState(false);
  const [feedbackVersion, setFeedbackVersion] = useState('v1');
  const [isUploadingEvidence, setIsUploadingEvidence] = useState(false);
  const [canAttachEvidence, setCanAttachEvidence] = useState(false);
  const [evidenceMessage, setEvidenceMessage] = useState('');
  const [evidenceDeadline, setEvidenceDeadline] = useState('');
  const [evidenceMeta, setEvidenceMeta] = useState({
    company: '',
    interviewType: '',
    denuncia: '',
    entrevistaId: '',
    nome: ''
  });
  const fileInputRef = useRef(null);

  useEffect(() => {
    const resolveCompanyPath = () => {
      if (company && typeof company === 'string') return company.toLowerCase();
      const pathParts = (window.location.pathname || '').split('/').filter(Boolean);
      return pathParts[0] || 'materdei';
    };

    const parseRouteParams = () => {
      const pathname = window.location.pathname || '';
      const match = pathname.match(/^\/([^/]+)\/([^/]+)\/([^/]+)\/entrevista\/([^/]+)$/);
      if (!match) return {};
      return {
        company: match[1],
        interviewType: match[2],
        denuncia: match[3],
        entrevistaId: match[4]
      };
    };

    const parseDeadline = (value) => {
      if (!value) return null;
      if (typeof value === 'string') {
        const parsed = new Date(value);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
      }
      if (value?.toDate) return value.toDate();
      if (typeof value === 'object' && value.seconds) {
        return new Date(value.seconds * 1000);
      }
      return null;
    };

    const buildStorageLogoUrl = (bucket) => {
      if (!bucket) return '';
      const companyPath = resolveCompanyPath();
      const objectPath = caminhoDoLogo(companyPath);
      if (bucket.endsWith('firebasestorage.app')) {
        return `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodeURIComponent(objectPath)}?alt=media`;
      }
      return `https://storage.googleapis.com/${bucket}/${objectPath}`;
    };

    let cancelled = false;
    let hasLogoFromParams = false;
    const params = new URLSearchParams(window.location.search);
    const encryptedData = params.get("data");
    const companyPath = resolveCompanyPath();
      const routeParams = parseRouteParams();
      let resolvedMeta = {
        company: routeParams.company || companyPath,
        interviewType: normalizeInterviewTypeKey(routeParams.interviewType || 'pesquisa-de-compliance'),
        denuncia: routeParams.denuncia || 'sem_denuncia',
        entrevistaId: routeParams.entrevistaId || 'default'
      };

    if (encryptedData) {
      try {
        const secretKey = "due-prd"; 
        const decodedData = decodeURIComponent(encryptedData); 
        const decryptedBase64 = CryptoJS.AES.decrypt(decodedData, secretKey).toString(CryptoJS.enc.Utf8);
        const decryptedString = atob(decryptedBase64); 
        const decryptedParams = JSON.parse(decryptedString); 

        const logoUrl = decryptedParams.logoCompanyUrl;
        if (logoUrl) {
          setLogoCompanyUrl(logoUrl);
          hasLogoFromParams = true;
        }
        resolvedMeta = {
          ...resolvedMeta,
          company: decryptedParams.company?._value || decryptedParams.company || resolvedMeta.company,
          interviewType: normalizeInterviewTypeKey(
            decryptedParams.entrevista || decryptedParams.tipoEntrevista || resolvedMeta.interviewType
          ),
          denuncia: decryptedParams.denuncia || resolvedMeta.denuncia,
          entrevistaId: decryptedParams.entrevistaId || resolvedMeta.entrevistaId
        };
      } catch (e) {
        console.error("Erro ao descriptografar os parâmetros da URL", e);
      }
    }

    const fetchLogoUrl = async () => {
      if (hasLogoFromParams) return;
      const bucket = process.env.REACT_APP_STORAGE_BUCKET;
      const publicUrl = bucket ? buildStorageLogoUrl(bucket) : '';
      if (!cancelled) {
        if (publicUrl) {
          setLogoCompanyUrl(publicUrl);
        } else {
          console.error("Parâmetros não encontrados na URL");
        }
      }
    };

    const fetchInterviewMeta = async () => {
      try {
        // RS6: essa leitura era um getDoc() direto do client no path LEGADO
        // (cliente/{company}/...) — mesma classe de bug do RS1/RS7. A página
        // pública roda sem Firebase Auth de propósito, então um candidato real
        // sempre batia em permission-denied aqui (e, mesmo se a leitura
        // passasse, o path legado está vazio pra qualquer entrevista criada
        // pela UI atual, que grava no path canônico). Resultado: a tela de
        // "Enviar evidências" nunca aparecia de verdade pra ninguém — sempre
        // caía no catch abaixo com "Não foi possível carregar os dados".
        // Corrigido reaproveitando o mesmo callable server-side já usado pra
        // abrir a entrevista (getInterviewByAccessToken via
        // fetchInterviewByAccessToken), que resolve canônico-então-legado e
        // já valida o token — nenhuma leitura direta de Firestore aqui.
        const token = (new URLSearchParams(window.location.search).get('t') || '').toString().trim();
        const result = token
          ? await fetchInterviewByAccessToken({
              company: resolvedMeta.company,
              interviewTypeKey: normalizeInterviewTypeKey(resolvedMeta.interviewType),
              denuncia: resolvedMeta.denuncia,
              entrevistaId: resolvedMeta.entrevistaId,
              token
            })
          : null;
        const data = result?.interview || null;
        if (!data) {
          setEvidenceMessage('Não foi possível carregar os dados da entrevista.');
          return;
        }
        let prazo = parseDeadline(data.prazoLimite || data.prazoFinal || data.prazoFim || data.deadlineAt || data.deadline);
        if (!prazo && result?.denuncia?.deadline) {
          prazo = parseDeadline(result.denuncia.deadline);
        }
        const dentroDoPrazo = !prazo || prazo.getTime() >= Date.now();
        const pesquisaFinalizada = Boolean(data.pesquisaFinalizada || data.finalizada || data.encerrada);
        const canAttach = !pesquisaFinalizada && dentroDoPrazo;
        setCanAttachEvidence(canAttach);
        setFeedbackVersion(data.scriptVersion || data.versaoScript || data.version || 'v1');
        setEvidenceDeadline(prazo ? prazo.toLocaleDateString('pt-BR') : '');
        setEvidenceMeta((prev) => ({
          ...prev,
          ...resolvedMeta,
          nome: data.nome || prev.nome
        }));
        if (!canAttach) {
          setEvidenceMessage(
            !dentroDoPrazo
              ? 'O prazo para anexar evidências encerrou.'
              : 'A pesquisa já foi finalizada.'
          );
        }
      } catch (err) {
        console.error('Erro ao buscar dados da entrevista:', err);
        setEvidenceMessage('Não foi possível carregar os dados da entrevista.');
      }
    };

    fetchLogoUrl();
    fetchInterviewMeta();
    return () => {
      cancelled = true;
    };
  }, [company]);

  useEffect(() => {
    let cancelled = false;
    const fetchFeedbackStatus = async () => {
      if (!evidenceMeta.company || !evidenceMeta.interviewType || !evidenceMeta.entrevistaId) {
        return;
      }
      try {
        const db = getFirestore();
        const feedbackRef = doc(
          db,
          caminhoDoMeta({
            empresa: evidenceMeta.company || company || 'materdei',
            tipo: normalizeInterviewTypeKey(evidenceMeta.interviewType || 'pesquisa-de-compliance'),
          }),
          'feedback',
          feedbackVersion || 'v1',
          'responses',
          evidenceMeta.entrevistaId || 'default'
        );
        const feedbackSnap = await getDoc(feedbackRef);
        if (!cancelled && feedbackSnap.exists()) {
          setFeedbackSaved(true);
        }
      } catch (err) {
        console.error('Erro ao buscar status do feedback:', err);
      } finally {
        if (!cancelled) {
          setFeedbackLoaded(true);
        }
      }
    };
    fetchFeedbackStatus();
    return () => {
      cancelled = true;
    };
  }, [company, evidenceMeta.company, evidenceMeta.interviewType, evidenceMeta.entrevistaId, feedbackVersion]);

  const sanitizeName = (str = '') => (str || 'arquivo')
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9._-]/g, '_')
    .replace(/_{2,}/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 100) || 'arquivo';

  const triggerFileSelect = () => {
    if (!canAttachEvidence || isUploadingEvidence) return;
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const uploadSelectedFiles = async (filesArray) => {
    if (!filesArray.length) return;
    if (isUploadingEvidence) return;
    setIsUploadingEvidence(true);
    try {
      const functions = getFunctions(app, "southamerica-east1");
      const getEvidenceUploadUrls = httpsCallable(functions, 'getEvidenceUploadUrls');
      const filesPayload = filesArray.map((file) => ({
        name: sanitizeName(file.name),
        contentType: file.type || 'application/octet-stream'
      }));
      const { data } = await getEvidenceUploadUrls({
        company: evidenceMeta.company || company || 'materdei',
        denuncia: evidenceMeta.denuncia || 'sem_denuncia',
        nome: evidenceMeta.nome || 'anonimo',
        files: filesPayload
      });

      const urls = data?.urls || [];
      const uploadPromises = urls.map(async ({ name, url }) => {
        const file = filesArray.find((f) => sanitizeName(f.name) === name);
        if (!file) return;
        const resp = await fetch(url, {
          method: 'PUT',
          headers: { 'Content-Type': file.type || 'application/octet-stream' },
          body: file
        });
        if (!resp.ok) {
          const text = await resp.text().catch(() => '');
          throw new Error(`Upload falhou (${resp.status} ${resp.statusText}) ${text}`);
        }
      });

      await Promise.all(uploadPromises);
      setEvidenceMessage('Evidências enviadas com sucesso.');
    } catch (error) {
      console.error('Erro ao enviar evidências:', error);
      setEvidenceMessage('Erro ao enviar evidências. Tente novamente.');
    }
    setIsUploadingEvidence(false);
  };

  const handleFileSelection = (event) => {
    const filesArray = Array.from(event.target.files || []);
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
    uploadSelectedFiles(filesArray);
  };

  const feedbackOptions = [
    'Prazos e tempo para responder',
    'Clareza das perguntas',
    'Quantidade de perguntas',
    'Facilidade de uso (navegação)',
    'Qualidade do áudio/vídeo',
    'Confiança na confidencialidade',
    'Uso do microfone/ditado',
    'Velocidade dos vídeos'
  ];

  const toggleFeedbackNote = (option) => {
    setFeedbackNotes((prev) => {
      const next = new Set(prev);
      if (next.has(option)) {
        next.delete(option);
      } else {
        next.add(option);
      }
      return next;
    });
  };

  const handleStarClick = (value) => {
    if (feedbackSaved) return;
    setFeedbackRating(value);
    if (value === 5) {
      saveFeedback(value);
    }
  };

  const renderStars = () => (
    <Stars>
      {[1, 2, 3, 4, 5].map((value) => (
        <StarButton
          key={value}
          type="button"
          aria-label={`${value} estrela${value > 1 ? 's' : ''}`}
          $active={feedbackRating >= value}
          onClick={() => handleStarClick(value)}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 2.6l2.77 5.62 6.2.9-4.48 4.37 1.06 6.17L12 16.9 6.45 19.66l1.06-6.17-4.48-4.37 6.2-.9L12 2.6z" />
          </svg>
        </StarButton>
      ))}
    </Stars>
  );

  const saveFeedback = async (ratingOverride) => {
    const rating = typeof ratingOverride === 'number' ? ratingOverride : feedbackRating;
    if (!rating || feedbackSaving || feedbackSaved) return;
    setFeedbackSaving(true);
    try {
      const db = getFirestore();
      const docRef = doc(
        db,
        caminhoDoMeta({
          empresa: evidenceMeta.company || company || 'materdei',
          tipo: normalizeInterviewTypeKey(evidenceMeta.interviewType || 'pesquisa-de-compliance'),
        }),
        'feedback',
        feedbackVersion || 'v1',
        'responses',
        evidenceMeta.entrevistaId || 'default'
      );
      await setDoc(docRef, {
        rating,
        notes: Array.from(feedbackNotes),
        text: feedbackText,
        interviewType: normalizeInterviewTypeKey(evidenceMeta.interviewType || 'pesquisa-de-compliance'),
        company: evidenceMeta.company || company || 'materdei',
        denuncia: evidenceMeta.denuncia || 'sem_denuncia',
        entrevistaId: evidenceMeta.entrevistaId || 'default',
        scriptVersion: feedbackVersion || 'v1',
        createdAt: serverTimestamp()
      }, { merge: true });
      setFeedbackSaved(true);
    } catch (error) {
      console.error('Erro ao salvar feedback:', error);
    } finally {
      setFeedbackSaving(false);
    }
  };
  return (
    <EndContainer>
      {logoCompanyUrl ? (
        <img src={logoCompanyUrl} alt="Company Logo" onError={() => setLogoCompanyUrl('')} />
      ) : (
        <p>Logo não encontrada nos parâmetros da URL</p>
      )}
      <h1>Agradecemos sua participação.</h1>
      <p>
        Você pode abrir este link novamente para anexar evidências relacionadas a esta entrevista
        {evidenceDeadline ? ` até ${evidenceDeadline}.` : ', enquanto a pesquisa estiver dentro do prazo.'}
      </p>
      {canAttachEvidence && (
        <>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileSelection}
            style={{ display: 'none' }}
          />
          <button type="button" onClick={triggerFileSelect} disabled={isUploadingEvidence}>
            {isUploadingEvidence ? 'Enviando...' : 'Enviar evidências'}
          </button>
        </>
      )}
      {evidenceMessage && <p>{evidenceMessage}</p>}
      <FeedbackSection>
        <h2>O que você achou do modelo de entrevista virtual?</h2>
        {!feedbackSaved && renderStars()}
        {feedbackRating > 0 && feedbackRating < 5 && !feedbackSaved && (
          <FeedbackForm>
            <p>Marque apenas o que voc&#234; entende que precisa melhorar.</p>
            <CheckboxGrid>
              {feedbackOptions.map((option) => (
                <label key={option}>
                  <input
                    type="checkbox"
                    checked={feedbackNotes.has(option)}
                    onChange={() => toggleFeedbackNote(option)}
                    disabled={feedbackSaved}
                  />
                  {option}
                </label>
              ))}
            </CheckboxGrid>
            <TextArea
              value={feedbackText}
              onChange={(event) => setFeedbackText(event.target.value)}
              placeholder="Observa&#231;&#245;es (opcional)"
              rows={3}
              disabled={feedbackSaved}
            />
          </FeedbackForm>
        )}
        {feedbackRating > 0 && feedbackRating < 5 && !feedbackSaved && (
          <FeedbackButton
            type="button"
            onClick={() => saveFeedback()}
            disabled={feedbackSaving || feedbackSaved}
          >
            {feedbackSaved ? 'Feedback enviado' : (feedbackSaving ? 'Enviando...' : 'Enviar feedback')}
          </FeedbackButton>
        )}
        {feedbackSaved && feedbackLoaded && (
          <FeedbackSuccess>Feedback j&#225; registrado para esta entrevista.</FeedbackSuccess>
        )}
      </FeedbackSection>
    </EndContainer>
  );
}

const EndContainer = styled.div`
  height: 100vh;
  width: 100vw;
  background-color: #F8F9FB;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 25px;
  img {
    width: 250px;
    height: 250px;
    object-fit: contain;
  }
  h1 {
    font-size: 42px;
    color: #333;
    font-family: "Poppins", sans-serif;
  }
  p {
    font-size: 24px;
    color: #666;
    font-family: "Poppins", sans-serif;
    margin-top: 10px;
  }
  button {
    margin-top: 18px;
    background: #2c3e50;
    color: #fff;
    border: none;
    border-radius: 10px;
    padding: 12px 20px;
    font-size: 16px;
    font-family: "Poppins", sans-serif;
    cursor: pointer;
  }
  button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const FeedbackSection = styled.section`
  margin-top: 30px;
  width: min(640px, 90vw);
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
  text-align: center;

  h2 {
    font-size: 24px;
    color: #1f2d3d;
    font-family: "Poppins", sans-serif;
  }
`;

const Stars = styled.div`
  width: 100%;
  display: flex;
  gap: 0.75px;
  justify-content: center;
  align-items: center;
  flex-wrap: nowrap;
  padding: 0 16px;
  box-sizing: border-box;
`;

const StarButton = styled.button`
  border: none;
  background: transparent !important;
  padding: 0;
  margin: 0;
  border-radius: 0;
  cursor: pointer;
  transition: transform 0.1s ease, color 0.2s ease;

  &:hover {
    transform: scale(1.05);
  }

  svg {
    width: 44px;
    height: 44px;
    fill: ${props => (props.$active ? '#f4b400' : 'transparent')};
    stroke: #f4b400;
    stroke-width: 1.6;
  }
`;

const FeedbackForm = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CheckboxGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 10px;
  text-align: left;

  label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 16px;
    color: #425466;
    font-family: "Poppins", sans-serif;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  border-radius: 10px;
  border: 1px solid #d0d7de;
  padding: 12px;
  font-size: 16px;
  font-family: "Poppins", sans-serif;
  resize: vertical;
`;

const FeedbackButton = styled.button`
  margin-top: 6px;
  background: #2c3e50;
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 10px 18px;
  font-size: 16px;
  font-family: "Poppins", sans-serif;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const FeedbackSuccess = styled.p`
  font-size: 16px;
  color: #2f855a;
  font-family: "Poppins", sans-serif;
  margin-top: 6px;
`;
